import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import {PMCItemModel} from "./shared/models/pmc-item.model";
import {PMCService} from "./shared/service/pmc/pmc.service";
import {finalize, Subscription} from "rxjs";
import {PMCModel} from "./shared/models/pmc.model";
import {Page} from "./shared/models/page.model";
import {CommonModule} from '@angular/common';
import {RouterModule} from '@angular/router';
import {DataTableModule} from '@bhplugin/ng-datatable';
import {IconModule} from './shared/icon/icon.module';
import {NgApexchartsModule} from 'ng-apexcharts';
import {NgLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent} from "@ng-select/ng-select";
import {FormsModule} from '@angular/forms';

@Component({
    templateUrl: './index.html',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule, DataTableModule, IconModule, NgApexchartsModule, NgOptionTemplateDirective, NgSelectComponent, NgLabelTemplateDirective]
})
export class IndexComponent implements OnInit, OnDestroy {

    private readonly sub$: Subscription = new Subscription();

    store: any;
    salesByCategory: any;
    dailyTrend: any;
    isLoading = true;

    pmcs: PMCModel[] = [];
    selectedPmc: PMCModel | null = null;
    durationOptions = [7, 14, 30];
    selectedDuration = 7;

    pmcItems: PMCItemModel[] = [];

    cols = [
        {field: 'name', title: 'Name'},
        {field: 'frequency', title: 'Frequency'},
        {field: 'firstDate', title: 'First Date'},
        {field: 'endDate', title: 'End Date'},
        {field: 'shifts', title: 'Shifts'},
        {field: 'actions', title: 'Actions', sort: false, headerClass: 'justify-center'},
    ];

    private readonly pmcService: PMCService = inject(PMCService);
    private readonly storeData: Store<any> = inject(Store);

    ngOnInit() {
        this.getAllPmcs();
        this.initStore();
    }

    ngOnDestroy() {
        this.sub$.unsubscribe();
    }

    private getAllPmcs(): void {
        this.isLoading = true;
        this.sub$.add(
            this.pmcService
                .getAllPmcs(0, 10)
                .pipe(finalize(() => this.isLoading = false))
                .subscribe((page: Page<PMCModel>) => {
                    if (page) {
                        this.pmcs = page.content;
                        if (this.pmcs.length > 0 && !this.selectedPmc) {
                            this.selectedPmc = this.pmcs[0];
                        }
                    }
                }),
        );
    }

    async initStore() {
        this.storeData
            .select((d) => d.index)
            .subscribe((d) => {
                const hasChangeTheme = this.store?.theme !== d?.theme;
                const hasChangeLayout = this.store?.layout !== d?.layout;
                const hasChangeMenu = this.store?.menu !== d?.menu;
                const hasChangeSidebar = this.store?.sidebar !== d?.sidebar;

                this.store = d;

                if (hasChangeTheme || hasChangeLayout || hasChangeMenu || hasChangeSidebar) {
                    if (hasChangeTheme) {
                        this.loadCharts(); //init charts
                    } else {
                        setTimeout(() => {
                            this.loadCharts(); // refresh charts
                        }, 300);
                    }
                }
            });
    }

    loadCharts(page = 0, size = 500) {
        this.isLoading = true;

        const now = new Date();
        const endDate = now.toISOString().split('T')[0];
        const startDate = new Date(
            now.getTime() - (this.selectedDuration - 1) * 24 * 60 * 60 * 1000
        ).toISOString().split('T')[0];

        this.sub$.add(this.pmcService.findAllPMCItems(page, size, startDate, endDate)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe(
                (response) => {
                    this.pmcItems = response?.content || [];
                    this.initCharts();
                }
            ));
    }

    initCharts() {
        const filteredItems = this.selectedPmc
            ? this.pmcItems.filter(item => item.pmc.id === this.selectedPmc?.id)
            : this.pmcItems;

        const pendingCount = filteredItems.filter(item => item.results.length === 0 && !item.noInspection).length;
        const completedItems = filteredItems.filter(item => item.results.length !== 0 || item.noInspection);
        const completedKoCount = completedItems.filter(item => this.hasNegativeChecks(item)).length;

        const isDark = this.store.theme === 'dark' || this.store.isDarkMode;

        // sales by category
        this.salesByCategory = {
            chart: {
                type: 'donut',
                height: 400,
                fontFamily: 'Nunito, sans-serif',
            },
            dataLabels: {
                enabled: false,
            },
            stroke: {
                show: true,
                width: 25,
                colors: isDark ? '#0e1726' : '#fff',
            },
            colors: ['#808080', '#00E396', '#FF4560'], // Gray, Green, Red
            legend: {
                position: 'bottom',
                horizontalAlign: 'center',
                fontSize: '14px',
                markers: {
                    width: 10,
                    height: 10,
                    offsetX: -2,
                },
                height: 50,
                offsetY: 20,
            },
            plotOptions: {
                pie: {
                    donut: {
                        size: '65%',
                        background: 'transparent',
                        labels: {
                            show: true,
                            name: {
                                show: true,
                                fontSize: '29px',
                                offsetY: -10,
                            },
                            value: {
                                show: true,
                                fontSize: '26px',
                                color: isDark ? '#bfc9d4' : undefined,
                                offsetY: 16,
                                formatter: (val: any) => {
                                    return val;
                                },
                            },
                            total: {
                                show: true,
                                label: 'Total',
                                color: '#888ea8',
                                fontSize: '29px',
                                formatter: (w: any) => {
                                    return w.globals.seriesTotals.reduce(function (a: any, b: any) {
                                        return a + b;
                                    }, 0);
                                },
                            },
                        },
                    },
                },
            },
            labels: ['Pending', 'OK', 'Not Ok'],
            states: {
                hover: {
                    filter: {
                        type: 'none',
                        value: 0.15,
                    },
                },
                active: {
                    filter: {
                        type: 'none',
                        value: 0.15,
                    },
                },
            },
            series: [pendingCount, completedItems.length - completedKoCount, completedKoCount],
        };

        // Trend
        // Group items by equipment
        const seriesMap = new Map<string, { name: string, data: { x: Date, y: number, color: string }[] }>();

        filteredItems.forEach(item => {
            const equipmentKey = item?.equipment?.identification || item?.equipment?.name || 'Unknown Equipment';
            if (!seriesMap.has(equipmentKey)) {
                seriesMap.set(equipmentKey, {
                    name: equipmentKey,
                    data: []
                });
            }

            const series = seriesMap.get(equipmentKey)!;
            item.results.forEach(resultItem => {
                const resultValue = Number(resultItem?.result);
                if (Number.isFinite(resultValue)) {
                    const pointDate = resultItem?.date ? new Date(resultItem.date) : new Date(item.date);
                    console.log(`Processing result for ${equipmentKey} on ${pointDate.toISOString()}: ${resultValue}`);
                    series.data.push({
                        x: pointDate,
                        y: resultValue,
                        color: this.hasNegativeChecksWithValue(item, resultValue) ? '#FF4560' : '#00E396'
                    });
                }
            });
        });

        seriesMap.forEach(series => {
            series.data.sort((a, b) => a.x.getTime() - b.x.getTime());
        });

        const now = new Date();
        const minDate = new Date(now);
        minDate.setDate(now.getDate() - (this.selectedDuration - 1));
        minDate.setHours(0, 0, 0, 0);
        const maxDate = new Date(now);
        maxDate.setHours(23, 59, 59, 999);

        const checksSource =
            (this.selectedPmc?.checks && this.selectedPmc.checks.length > 0)
                ? this.selectedPmc.checks
                : (this.pmcs.find(pmc => pmc.checks && pmc.checks.length > 0)?.checks ?? []);
        const {minCriteria, maxCriteria} = this.calculateCriteriaBounds(checksSource);
        const {minData, maxData} = this.calculateSeriesBounds(seriesMap);
        const {yAxisMin, yAxisMax} = this.calculateYAxisBounds(minCriteria, maxCriteria, minData, maxData);

        this.dailyTrend = {
            chart: {
                height: 400,
                type: 'line',
                fontFamily: 'Nunito, sans-serif',
                toolbar: {
                    show: false,
                },
            },
            dataLabels: {
                enabled: false,
                background: {
                    enabled: true,
                    padding: 4,
                    borderRadius: 2,
                    borderWidth: 1,
                    borderColor: '#fff',
                    opacity: 0.9,
                    dropShadow: {
                        enabled: false,
                    },
                },
                style: {
                    fontSize: '12px',
                    fontFamily: 'Nunito, sans-serif',
                },
                formatter: (val: any) => {
                    return val !== 0 ? val : '';
                },
            },
            markers: {
                size: 4,
                strokeColors: '#fff',
                strokeWidth: 2,
                discrete: Array.from(seriesMap.values()).flatMap((s, seriesIndex) => {
                    return s.data.map((d, dataPointIndex) => ({
                        seriesIndex: seriesIndex,
                        dataPointIndex: dataPointIndex,
                        fillColor: d.y === 0 ? 'transparent' : d.color,
                        strokeColor: d.y === 0 ? 'transparent' : '#fff',
                        size: d.y === 0 ? 0 : 5
                    }));
                }),
            },
            stroke: {
                show: true,
                width: 2,
                curve: 'smooth'
            },
            colors: ['#888ea8'], // Neutral color
            responsive: [
                {
                    breakpoint: 480,
                    options: {
                        legend: {
                            position: 'bottom',
                            offsetX: -10,
                            offsetY: 0,
                        },
                    },
                },
            ],
            xaxis: {
                type: 'datetime',
                min: minDate.getTime(),
                max: maxDate.getTime(),
                labels: {
                    show: true,
                },
            },
            yaxis: {
                show: true,
                ...(yAxisMin !== undefined && yAxisMax !== undefined ? {min: yAxisMin, max: yAxisMax} : {}),
            },
            tooltip: {
                shared: true,
                intersect: false,
                y: {
                    formatter: (val: number) => {
                        return val !== undefined ? val.toFixed(2) : val;
                    },
                },
            },
            legend: {
                show: true,
                position: 'top',
                horizontalAlign: 'right',
            },
            grid: {
                show: true,
                xaxis: {
                    lines: {
                        show: false,
                    },
                },
                padding: {
                    top: 10,
                    right: 0,
                    bottom: 0,
                    left: 0,
                },
            },
            annotations: {
                yaxis: checksSource.flatMap((check) => ([
                    {
                        y: check.minAcceptanceCriteria,
                        borderColor: '#FF4560',
                        label: {
                            borderColor: '#FF4560',
                            style: {
                                color: '#fff',
                                background: '#FF4560',
                            },
                            text: check.minAcceptanceCriteria.toString(),
                        },
                    },
                    {
                        y: check.maxAcceptanceCriteria,
                        borderColor: '#FF4560',
                        label: {
                            borderColor: '#FF4560',
                            style: {
                                color: '#fff',
                                background: '#FF4560',
                            },
                            text: check.maxAcceptanceCriteria.toString(),
                        },
                    },
                ])),
            },
            series: Array.from(seriesMap.values()).map((s) => ({
                name: s.name,
                data: s.data.map((d) => ({
                    x: d.x,
                    y: d.y,
                    color: d.color
                }))
            })),
        };
    }

    onPmcChange(): void {
        this.initCharts();
    }

    onDurationChange(): void {
        this.loadCharts();
    }

    private hasNegativeChecks(item: PMCItemModel): boolean {
        if (item.noInspection) {
            return false;
        }
        const checks = item.pmc?.checks ?? [];
        const results = item.results ?? [];
        return checks.some(check =>
            results.some(resultItem => {
                const resultValue = Number(resultItem?.result);
                return (
                    Number.isFinite(resultValue) &&
                    (resultValue < check.minAcceptanceCriteria || resultValue > check.maxAcceptanceCriteria)
                );
            })
        );
    }

    private hasNegativeChecksWithValue(item: PMCItemModel, resultValue: number): boolean {
        if (item.noInspection) {
            return false;
        }
        const checks = item.pmc?.checks ?? [];
        return checks.some(check => {
                return (
                    Number.isFinite(resultValue) &&
                    (resultValue < check.minAcceptanceCriteria || resultValue > check.maxAcceptanceCriteria)
                );
            }
        );
    }

    private calculateCriteriaBounds(checksSource: PMCModel['checks']): { minCriteria?: number; maxCriteria?: number } {
        if (!checksSource || checksSource.length === 0) {
            return {};
        }

        return {
            minCriteria: Math.min(...checksSource.map(check => check.minAcceptanceCriteria)),
            maxCriteria: Math.max(...checksSource.map(check => check.maxAcceptanceCriteria)),
        };
    }

    private calculateSeriesBounds(
        seriesMap: Map<string, { name: string; data: { x: Date; y: number; color: string }[] }>
    ): { minData?: number; maxData?: number } {
        const dataValues = Array.from(seriesMap.values())
            .flatMap(series => series.data.map(point => point.y))
            .filter(Number.isFinite);

        if (dataValues.length === 0) {
            return {};
        }

        return {
            minData: Math.min(...dataValues),
            maxData: Math.max(...dataValues),
        };
    }

    private calculateYAxisBounds(
        minCriteria?: number,
        maxCriteria?: number,
        minData?: number,
        maxData?: number
    ): { yAxisMin?: number; yAxisMax?: number } {
        const yAxisMin = (minCriteria !== undefined && minData !== undefined)
            ? Math.min(minCriteria, minData)
            : (minCriteria ?? minData);
        const yAxisMax = (maxCriteria !== undefined && maxData !== undefined)
            ? Math.max(maxCriteria, maxData)
            : (maxCriteria ?? maxData);

        return {yAxisMin, yAxisMax};
    }

}
